import { useEffect, useState } from "react";
import { FiSave, FiTruck } from "react-icons/fi";
import { updateContainer } from "../services/containerApi";

const statuses = [
  "In Transit",
  "Arrived at Port",
  "Delivered",
  "Departed from Origin Port",
];

const dateValue = (value) => (value ? String(value).split("T")[0] : "");

export default function ContainerStatusUpdatePanel({
  container,
  canUpdate,
  limitedMode = false,
  onUpdated,
  onMessage,
}) {
  const [form, setForm] = useState({
    status: "In Transit",
    customCleared: false,
    expectedArrival: "",
    deliveryBookedOn: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!container) return;
    setForm({
      status: container.status || "In Transit",
      customCleared: Boolean(container.customCleared),
      expectedArrival: dateValue(container.expectedArrival),
      deliveryBookedOn: dateValue(container.deliveryBookedOn),
      description: container.description || "",
    });
  }, [container]);

  if (!canUpdate || !limitedMode || !container) return null;

  const save = async () => {
    setSaving(true);
    try {
      const response = await updateContainer(container.id, form);
      onMessage?.("Container progress updated successfully");
      onUpdated?.(response.data);
    } catch (error) {
      onMessage?.(error.response?.data?.message || "Unable to update container", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20">
      <div className="flex items-start gap-3 border-b border-amber-200 px-5 py-4 dark:border-amber-900">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-200">
          <FiTruck />
        </div>
        <div>
          <h4 className="font-bold text-slate-950 dark:text-white">Update Container Progress</h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Your limited account can only update progress fields and upload documents.
          </p>
        </div>
      </div>

      <div className="grid gap-4 p-5 md:grid-cols-2">
        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          Status
          <select
            value={form.status}
            onChange={(event) => setForm((old) => ({ ...old, status: event.target.value }))}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-950"
          >
            {statuses.map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-3 self-end rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold dark:border-slate-700 dark:bg-slate-950">
          <input
            type="checkbox"
            checked={form.customCleared}
            onChange={(event) =>
              setForm((old) => ({ ...old, customCleared: event.target.checked }))
            }
            className="h-4 w-4 rounded border-slate-300 text-emerald-600"
          />
          Customs cleared
        </label>

        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          ETA
          <input
            type="date"
            value={form.expectedArrival}
            onChange={(event) =>
              setForm((old) => ({ ...old, expectedArrival: event.target.value }))
            }
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-950"
          />
        </label>

        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          Delivery Booked On
          <input
            type="date"
            value={form.deliveryBookedOn}
            onChange={(event) =>
              setForm((old) => ({ ...old, deliveryBookedOn: event.target.value }))
            }
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-950"
          />
        </label>

        <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 md:col-span-2">
          Progress Notes
          <textarea
            rows="3"
            value={form.description}
            onChange={(event) =>
              setForm((old) => ({ ...old, description: event.target.value }))
            }
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-950"
          />
        </label>

        <button
          type="button"
          disabled={saving}
          onClick={save}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50 md:col-span-2"
        >
          <FiSave /> {saving ? "Saving..." : "Save Progress"}
        </button>
      </div>
    </section>
  );
}
